// Trivia Subpage - Interactive trivia questions about Revolt
// Follows the Section, Component & Manager System architecture

import React, { useState } from 'react';
import { MainLayout } from '../../../../components/Layout';
import { LearnManager } from '../../managers/learn-manager';
import { useManager } from '../../../../core';
import { LearnHeader, LearnPageHeader, SearchBar, LearnLoading, TriviaItem } from '../../components';
import '../../LearnShared.css';

export function TriviaSection() {
  const { manager, isInitialized } = useManager(LearnManager);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [showResults, setShowResults] = useState({});
  const [localTriviaData, setLocalTriviaData] = useState({});

  if (!isInitialized) {
    return (
      <MainLayout contentClassName="main-content--learn">
        <LearnLoading
          title="Loading Trivia"
          subtitle="Preparing fun challenges..."
        />
      </MainLayout>
    );
  }

  const filteredTrivia = manager.getFilteredTriviaQuestions();

  // Merge manager data with local updates for real-time stats
  const triviaWithUpdates = filteredTrivia.map(trivia => ({
    ...trivia,
    attempts: (trivia.attempts || 0) + (localTriviaData[trivia.id]?.attempts || 0),
    correctAttempts: (trivia.correctAttempts || 0) + (localTriviaData[trivia.id]?.correctAttempts || 0)
  }));

  const handleAnswerSelect = (questionId, answer, questionType) => {
    setSelectedAnswers(prev => {
      const currentTrivia = triviaWithUpdates.find(t => t.id === questionId);
      const type = questionType || currentTrivia?.questionType;

      if (type === 'multi-select') {
        // Multi-select: toggle selection in array
        const currentSelections = prev[questionId] || [];
        const isSelected = currentSelections.includes(answer);
        const newSelections = isSelected
          ? currentSelections.filter(idx => idx !== answer)
          : [...currentSelections, answer];

        return {
          ...prev,
          [questionId]: newSelections
        };
      } else if (type === 'ordered-list') {
        // Ordered list: direct array assignment
        return {
          ...prev,
          [questionId]: answer
        };
      } else {
        // Single-select types: direct value assignment
        return {
          ...prev,
          [questionId]: answer
        };
      }
    });
  };

  const handleSubmitAnswer = async (questionId, answer) => {
    try {
      // Submit answer to backend
      const response = await fetch(`/api/learn/trivia/${questionId}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ answer })
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.error || 'Failed to submit answer');
      }

      // Update local trivia stats
      setLocalTriviaData(prev => ({
        ...prev,
        [questionId]: {
          attempts: (prev[questionId]?.attempts || 0) + 1,
          correctAttempts: (prev[questionId]?.correctAttempts || 0) + (data.correct ? 1 : 0)
        }
      }));

      // Update local state
      setShowResults(prev => ({
        ...prev,
        [questionId]: true
      }));
    } catch (error) {
      console.error('Failed to submit answer:', error);
      alert('Failed to submit answer. Please try again.');
    }
  };


  return (
    <MainLayout contentClassName="main-content--learn">
      {/* Fixed Learn Page Header */}
      <LearnPageHeader />

      <div className="learn-container">
        {/* Learn Header with current section highlighted */}
        <LearnHeader />

        {/* Search Bar */}
        <SearchBar
          searchTerm={manager.searchTerm}
          onSearchChange={manager.setSearchTerm.bind(manager)}
          placeholder="Search trivia questions..."
        />


        {/* Trivia Content */}
        <div className="learn-trivia-section">
          {filteredTrivia.length === 0 ? (
            <div className="learn-empty-state">
              <i className="fa-solid fa-brain"></i>
              <h3>No trivia questions found</h3>
              <p>Try adjusting your search terms or check back later for new content.</p>
            </div>
          ) : (
            <div className="learn-trivia-list">
              {triviaWithUpdates.map((trivia) => (
                <TriviaItem
                  key={trivia.id}
                  trivia={trivia}
                  selectedAnswer={selectedAnswers[trivia.id]}
                  selectedAnswers={selectedAnswers[trivia.id]}
                  showResults={showResults[trivia.id]}
                  onAnswerSelect={(answer) => handleAnswerSelect(trivia.id, answer, trivia.questionType)}
                  onSubmitAnswer={(answer) => handleSubmitAnswer(trivia.id, answer)}
                  manager={manager}
                  disabled={false}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}